import { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Star, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATING_CATEGORIES = [
  { key: 'uniform_rating', label: 'Uniform' },
  { key: 'engagement_rating', label: 'Engagement' },
  { key: 'information_rating', label: 'Information' },
  { key: 'communication_rating', label: 'Communication' },
  { key: 'professionalism_rating', label: 'Professionalism' },
];

function StarRating({ value, onChange, testId }) {
  return (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1 focus:outline-none"
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star
            className={`w-8 h-8 sm:w-9 sm:h-9 ${
              n <= value ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'
            }`}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

export default function GeneralFeedbackPage() {
  const today = new Date().toISOString().substring(0, 10);
  const [form, setForm] = useState({
    reviewer_type: 'public',
    location_or_event: '',
    feedback_date: today,
    uniform_rating: 0,
    engagement_rating: 0,
    information_rating: 0,
    communication_rating: 0,
    professionalism_rating: 0,
    comment: '',
    name: '',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const validate = () => {
    if (!form.location_or_event.trim()) {
      toast.error('Please enter the location or event name');
      return false;
    }
    if (!form.feedback_date) {
      toast.error('Please pick a date');
      return false;
    }
    for (const c of RATING_CATEGORIES) {
      if (!form[c.key] || form[c.key] < 1) {
        toast.error(`Please rate: ${c.label}`);
        return false;
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/feedback`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md bg-card border-border rounded-sm">
          <CardContent className="p-6 sm:p-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" strokeWidth={1.5} />
            <h1 className="text-2xl font-barlow tracking-widest uppercase">Thank You</h1>
            <p className="text-muted-foreground">Your feedback has been received.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-6 px-4">
      <div className="max-w-xl mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-2xl sm:text-3xl font-barlow tracking-widest uppercase text-foreground">
            Security Staff Feedback
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Please rate your experience. Your response is anonymous unless you choose to leave your name.
          </p>
        </div>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">I am *</Label>
                  <Select value={form.reviewer_type} onValueChange={(v) => setField('reviewer_type', v)}>
                    <SelectTrigger data-testid="reviewer-type-select" className="bg-background border-border rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-sm">
                      <SelectItem value="public">Member of the public</SelectItem>
                      <SelectItem value="staff">Staff member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Date *</Label>
                  <Input
                    type="date"
                    value={form.feedback_date}
                    onChange={(e) => setField('feedback_date', e.target.value)}
                    className="bg-background border-border rounded-sm"
                    data-testid="feedback-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Location or event *</Label>
                <Input
                  value={form.location_or_event}
                  onChange={(e) => setField('location_or_event', e.target.value)}
                  placeholder="e.g. Newcastle Eldon Square, Festival 2026..."
                  className="bg-background border-border rounded-sm"
                  data-testid="feedback-location-input"
                />
              </div>

              <div className="space-y-3 pt-2 border-t border-border">
                {RATING_CATEGORIES.map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-3">
                    <Label className="text-sm uppercase tracking-wider flex-1">{c.label} *</Label>
                    <StarRating
                      value={form[c.key]}
                      onChange={(v) => setField(c.key, v)}
                      testId={`rating-${c.key}`}
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-2 border-t border-border">
                <Label className="text-xs uppercase tracking-wider">Comment (optional)</Label>
                <Textarea
                  value={form.comment}
                  onChange={(e) => setField('comment', e.target.value)}
                  placeholder="Tell us more..."
                  rows={3}
                  className="bg-background border-border rounded-sm"
                  data-testid="feedback-comment-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Name (optional)</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="Leave blank to stay anonymous"
                    className="bg-background border-border rounded-sm"
                    data-testid="feedback-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Email (optional)</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="Only if you'd like a reply"
                    className="bg-background border-border rounded-sm"
                    data-testid="feedback-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm h-12 text-base font-semibold tracking-wide"
                data-testid="submit-feedback-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
